import { memo, useCallback } from 'react';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './styles.module.less';
import { openProductDetailModal } from '../../../../../../../../store/products/products.actions';
import { useScanVerificationContext } from '../../scan-verification-context-provider';

import type { CellProps } from './types';

const ItemNameCell = ({ row }: CellProps) => {
  const dispatch = useDispatch();
  const { isItemVerified } = useScanVerificationContext();
  const item = row.original;
  const isVerified = isItemVerified(item.fulfillmentPlanItemId);

  const handleOpenProductDetails = useCallback(() => {
    if (item.productId) {
      dispatch(openProductDetailModal(item.productId));
    }
  }, [dispatch, item.productId]);

  return (
    <FlexColumn>
      <FlexRow className={style['item-name']} gap={Gap.Space8}>
        {item.name ? (
          <>
            <Button
              className={style['item-name-link']}
              onClick={handleOpenProductDetails}
              theme={item.productId ? THEME_CONSTANTS.link : THEME_CONSTANTS.unstyled}
            >
              {item.name}
            </Button>
            {isVerified ? <FontAwesomeIcon className={style['item-name-verified-icon']} icon={faCheckCircle} /> : null}
          </>
        ) : null}
      </FlexRow>
      <FlexRow className={style['item-sku']} gap={Gap.Space8}>
        SKU: {item.sku}
        {isVerified && !item.name ? <FontAwesomeIcon className={style['item-name-verified-icon']} icon={faCheckCircle} /> : null}
      </FlexRow>
      {item.upc ? <div className={style['item-upc']}>UPC: {item.upc}</div> : null}
    </FlexColumn>
  );
};

export const createItemNameColumnDefinition = createColumnDefinitionBuilder({
  accessor: 'name',
  Header: <FormattedMessage {...messages.itemNameColumnHeader} />,
  Cell: memo(ItemNameCell),
});
